import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faEllipsis,
  faEyeSlash,
  faPen,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { usePreferredLineupStyle } from 'hooks/usePreferredLineupStyle';
import { getComposeTeamRoute } from 'lib/so5';

import type { LineupActionsDropdown_so5LeaderboardContender } from './__generated__/index.graphql';
import { InlineLineupIcon } from './assets/InlineLineupIcon';
import { SpreadLineupIcon } from './assets/SpreadLineupIcon';

const Root = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-nd-50-opaque);
  border-radius: var(--unit);

  & > * {
    flex: 1;
    &:not(:last-child) {
      border-bottom: 1px solid var(--c-nd-50);
    }
  }
`;
const StyledButtonBase = styled(ButtonBase)`
  display: block;
  min-width: 180px;
  padding: var(--unit) var(--double-unit);
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: rgba(var(--c-rgb-white), 0.05);
  }

  > ${Horizontal} {
    svg {
      width: var(--double-unit);
    }
  }
`;

type Props = {
  forceMode?: LineupStyle;
  hasEditLineupButton?: boolean;
  hasHideTeamButton?: boolean;
  showHideTeamDialog: () => void;
  so5LeaderboardContender?: LineupActionsDropdown_so5LeaderboardContender | null;
};
export const LineupActionsDropdown = ({
  forceMode,
  hasEditLineupButton,
  hasHideTeamButton,
  showHideTeamDialog,
  so5LeaderboardContender,
}: Props) => {
  const { sport } = useSportContext();
  const [lineupStyle, setLineupStyle] = usePreferredLineupStyle();

  const { so5Leaderboard, managerTeam, so5Lineup } =
    so5LeaderboardContender || {};

  const content = (mode: LineupStyle) => (
    <Root>
      {!!(hasEditLineupButton || hasHideTeamButton) && (
        <>
          {!!hasHideTeamButton && (
            <StyledButtonBase onClick={showHideTeamDialog}>
              <Horizontal>
                <FontAwesomeIcon icon={faEyeSlash} />
                <LabelL>
                  <FormattedMessage {...fantasy.hideTeam} />
                </LabelL>
              </Horizontal>
            </StyledButtonBase>
          )}
          {!!hasEditLineupButton && (
            <StyledButtonBase
              to={
                so5Leaderboard
                  ? getComposeTeamRoute({
                      leaderboardSlug: so5Leaderboard?.slug,
                      eventType: so5Leaderboard?.so5Fixture.type.toLowerCase(),
                      managerTeamId: managerTeam?.id,
                      lineupId: so5Lineup?.id,
                      sport,
                    })
                  : ''
              }
            >
              <Horizontal>
                <FontAwesomeIcon icon={faPen} />
                <LabelL>
                  <FormattedMessage {...fantasy.editLineup} />
                </LabelL>
              </Horizontal>
            </StyledButtonBase>
          )}
        </>
      )}
      <StyledButtonBase
        onClick={() => setLineupStyle(mode === 'inline' ? 'spread' : 'inline')}
      >
        <Horizontal>
          {mode === 'inline' ? <SpreadLineupIcon /> : <InlineLineupIcon />}
          <LabelL>
            {mode === 'inline' ? (
              <FormattedMessage
                id="LineupLayout.LineupLayoutHeader.stadiumView"
                defaultMessage="Stadium View"
              />
            ) : (
              <FormattedMessage
                id="LineupLayout.LineupLayoutHeader.lineView"
                defaultMessage="Line View"
              />
            )}
          </LabelL>
        </Horizontal>
      </StyledButtonBase>
    </Root>
  );

  if (forceMode) {
    return content(forceMode);
  }

  return (
    <Dropdown
      forceDesktopBehavior
      align="right"
      label={props => (
        <IconButton
          {...props}
          icon={faEllipsis}
          size="compact"
          color="transparent"
        />
      )}
    >
      {content(lineupStyle)}
    </Dropdown>
  );
};

LineupActionsDropdown.fragments = {
  so5LeaderboardContender: gql`
    fragment LineupActionsDropdown_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
      }
      so5Leaderboard {
        slug
        so5Fixture {
          slug
          type
        }
      }
      managerTeam {
        id
      }
    }
  ` as TypedDocumentNode<LineupActionsDropdown_so5LeaderboardContender>,
};
